// src/lib/catalogI18n.ts
//
// Lazy-loaded translations for product names in non-English locales.
//
// The backend endpoint GET /api/catalog-i18n/<lang> serves reviewed
// PostgreSQL translations as a per-SKU map of public text fields
// {name, full_name, category}. When a translation is
// present for the active language ("fr" / "nl") we substitute it in
// the catalog cards; English remains untouched.
//
// Design notes:
//   * The fetch is cached at module level so every ProductCard on the
//     page shares one network round-trip (the network call is made
//     once per page mount per language).
//   * We never block the EN rendering — `useCatalogProductName` returns
//     the original fallback synchronously, and rerenders only after
//     the translation resolves.
//   * Failures, missing database translations, and unknown languages all
//     fall back to the original product name.

import { useEffect, useState } from "react";
import i18n from "@/i18n";

/** One public database-backed row from /api/catalog-i18n/<lang>.
 *  Hidden products/brands and stale source fields are excluded by the API. */
export interface CatalogTranslationRow {
  /** Short product name, displayed in cards/grid. */
  name?: string;
  /** Full product name, typically the FileMaker "full_name". */
  full_name?: string;
  /** Localised category label (vendor category). */
  category?: string;
}

export type CatalogTranslationMap = Record<string, CatalogTranslationRow>;

/** Locales with catalog translations. English is the canonical
 *  source and intentionally excluded. */
const TRANSLATED_LOCALES = new Set<SupportedLocale>(["fr", "nl"]);

export type SupportedLocale = "fr" | "nl";

/** Module-level cache: language → in-flight or resolved promise.
 *  Concurrent ProductCards hitting the same language share one fetch. */
const fetchCache = new Map<SupportedLocale, Promise<CatalogTranslationMap>>();

function loadLocale(lang: SupportedLocale): Promise<CatalogTranslationMap> {
  const existing = fetchCache.get(lang);
  if (existing) return existing;
  const fresh: Promise<CatalogTranslationMap> = fetch(
    `/api/catalog-i18n/${lang}`,
  )
    .then((r): Promise<CatalogTranslationMap> => {
      if (!r.ok) return Promise.resolve({});
      return r.json() as Promise<CatalogTranslationMap>;
    })
    .then((data): CatalogTranslationMap =>
      data && typeof data === "object" ? data : {},
    )
    .catch((): CatalogTranslationMap => ({}));
  fetchCache.set(lang, fresh);
  return fresh;
}

/** Read the active i18n language and narrow it to one of the
 *  translated locales. Returns null for English (and any unknown
 *  language), which the hook treats as "no translation". */
function currentTranslatedLocale(): SupportedLocale | null {
  // i18n.language is e.g. "fr", "fr-FR", "nl-BE" — match the prefix.
  const lang = (i18n.language || "").toLowerCase().split(/[-_]/)[0];
  return TRANSLATED_LOCALES.has(lang as SupportedLocale)
    ? (lang as SupportedLocale)
    : null;
}

/**
 * Resolve the catalog-translated name for a single product.
 *
 * Returns the original English ``fallbackName`` synchronously; if a
 * translation is available for the active language and SKU, the
 * component rerenders with the translated value.
 *
 * Behaviour summary:
 *   * English (or any locale outside "fr"/"nl"): always returns
 *     ``fallbackName``.
 *   * French / Dutch with no reviewed database translations: returns the
 *     fallback — the backend returns ``{}`` for an unpopulated
 *     catalog.
 *   * French / Dutch with a missing SKU: also returns the fallback.
 *
 * Network failures (offline, 5xx) are caught silently; the fallback
 * wins in every error case.
 */
export function useCatalogProductName(
  sku: string,
  fallbackName: string,
): string {
  const locale = currentTranslatedLocale();
  const [translated, setTranslated] = useState<string | null>(null);

  useEffect(() => {
    // Reset whenever the SKU changes (e.g. infinite scroll loads a
    // new batch) so a stale translation from a previous row never
    // leaks into the next.
    setTranslated(null);
    if (!locale) return;
    let cancelled = false;
    loadLocale(locale).then((map) => {
      if (cancelled) return;
      const row = map[sku];
      // Prefer the short name (matches what cards show today);
      // fall back to full_name, then to the existing fallback.
      const next = row?.name || row?.full_name;
      if (next) setTranslated(next);
    });
    return () => {
      cancelled = true;
    };
  }, [locale, sku]);

  return translated ?? fallbackName;
}

/** Test/dev helper: forget cached locale fetches. */
export function _resetCatalogI18nCache(): void {
  fetchCache.clear();
}
